import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { CreditsPage, creditsTokenFromPath } from './components/CreditsPage.js';
import { UpdateBanner } from './components/UpdateBanner.js';
import { captureLinkFragment } from './core/accountLink.js';
import { standalonePage } from './core/approvalLink.js';
import { secretPage, stripFragment } from './core/owner.js';
import { rewritePackLink } from './core/packLink.js';
import './styles.css';

// Each page loads only its own code: the approval page must not pull in the
// studio (canvas, fonts, Turnstile), and the studio doesn't need the page.
const App = lazy(() => import('./App.js').then((m) => ({ default: m.App })));
const ApprovalPage = lazy(() => import('./components/ApprovalPage.js').then((m) => ({ default: m.ApprovalPage })));
const SecretPages = lazy(() => import('./components/SecretPagesEntry.js').then((m) => ({ default: m.SecretPagesEntry })));

const root = document.getElementById('root');
if (root === null) {
    throw new Error('storefront: #root mount point missing');
}

// Keep designs (K5): a `#tf_link=` coming back from litko.net is read and stripped before anything else runs (OMAR S4).
captureLinkFragment();

// `/pack/:handleTail` (a pack's product page, redirected by litko.net) opens the studio.
rewritePackLink();

// `/credits/:designToken` (R17) is a standalone page: no catalogue, no studio.
const creditsToken = creditsTokenFromPath(window.location.pathname);

// `/approve#<secret>` (late-proof approval): standalone, and up whatever the
// cart switch says — a paid pack waiting on it must never be stranded.
const standalone = standalonePage(window.location.pathname, window.location.hash);
if (standalone.kind === 'approval') {
    // A different link opened in the same tab only changes the fragment, which
    // loads nothing: start over so the page never shows the previous link's packs.
    window.addEventListener('hashchange', () => window.location.reload());
}

// `/s#<shareSecret>` (a shared design) and `/k#<recoverySecret>` (the recovery link):
// standalone, the secret read once and stripped from the address bar.
const secretOne = secretPage(window.location.pathname, window.location.hash);
if (secretOne.kind !== 'other') {
    stripFragment();
    window.addEventListener('hashchange', () => window.location.reload());
}

function page(): React.JSX.Element {
    if (standalone.kind === 'approval') {
        return <ApprovalPage secret={standalone.secret} />;
    }
    if (secretOne.kind !== 'other') {
        return <SecretPages page={secretOne} />;
    }
    if (creditsToken !== null) {
        return <CreditsPage designToken={creditsToken} />;
    }
    return <App />;
}

createRoot(root).render(
    <StrictMode>
        <UpdateBanner />
        <Suspense fallback={<div className="center-message"><h1>Loading…</h1></div>}>{page()}</Suspense>
    </StrictMode>,
);
